import { Card, Button, Text, VStack, HStack, Tag, TagVariant, Box, Icon, Wrap, WrapItem } from '@televet/kibble-ui';
import { TypographyVariant } from '@televet/kibble-ui/build/shared/types';
import { ModuleName } from '@televet/shared-types/productPackages';
import format from 'date-fns/format';
import partial from 'lodash-es/partial';
import useAppointmentChannel from 'pages/Appointments/hooks/useAppointmentChannel';
import React, { MouseEvent, useCallback, useMemo } from 'react';
import { PetAvatar } from 'shared/components/Avatars';
import ChannelStatusSelect from 'shared/components/ChannelStatusSelect/ChannelStatusSelect';
import HasModule from 'shared/components/HasModule';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { Placement } from 'shared/enums/Placement';
import { useModularity } from 'shared/hooks/useModularity';
import { usePhoneCallContext } from 'shared/providers/PhoneCallProvider';
import { useRecap } from 'shared/providers/RecapProvider/RecapProvider';
import { AppointmentStatus, ClinicPimsIntegrationType } from 'shared/types/graphql';
import { truncateString } from 'shared/utils';
import { Appointment } from '../..';

interface AppointmentCardProps {
  appointment: Appointment;
  onAppointmentClicked: (appointment: Appointment) => void;
  showTimeGroup: boolean;
}

const getAppointmentStatusTagVariant = (isPimsStatus: boolean, status: AppointmentStatus | string): TagVariant => {
  if (isPimsStatus) return 'info';

  switch (status) {
    case AppointmentStatus.Pending:
      return 'warning';
    case AppointmentStatus.Confirmed:
      return 'info';
    case AppointmentStatus.Completed:
      return 'success';
    default:
      return 'default';
  }
};

export const AppointmentCard = ({
  appointment,
  onAppointmentClicked,
  showTimeGroup,
}: AppointmentCardProps): JSX.Element => {
  const { viewClinicPet } = useSidePanelSearch();
  const { stageOutgoingPhoneCall } = usePhoneCallContext();
  const { toggleRecap, setRecapAppointmentId, isRecapActive } = useRecap();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);

  const {
    appointmentType,
    id: appointmentId,
    clinicPet,
    channel,
    status,
    clinicPetParents,
    description,
    pimsStatus,
    integration,
  } = appointment;

  const appointmentTime = useMemo(() => format(new Date(appointment.startAt), 'h:mm a'), [appointment.startAt]);

  const channelStatus = useMemo(() => channel?.channelStatus, [channel]);

  const appointmentTypeDisplayName = useMemo(() => {
    const displayName = appointmentType?.displayName || appointmentType?.name || 'None';

    if (displayName.length > 50) {
      return truncateString(displayName, 50);
    }

    return displayName;
  }, [appointmentType]);

  const petParentIds = useMemo(() => clinicPetParents.map(({ id }) => id), [clinicPetParents]);

  const petParentNames: string[] = useMemo(
    () => clinicPetParents.map(({ firstName, lastName }) => `${firstName + ' ' || ''}${lastName || ''}`),
    [clinicPetParents],
  );

  const onCallClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      const [clinicPetParent] = clinicPetParents;
      stageOutgoingPhoneCall({ clinicPetParentId: clinicPetParent.id });
    },
    [clinicPetParents, stageOutgoingPhoneCall],
  );
  const onRecapClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      if (!isRecapActive) {
        setRecapAppointmentId(appointmentId);
        toggleRecap();
      }
    },
    [appointmentId, isRecapActive, setRecapAppointmentId, toggleRecap],
  );

  const { onChatClick } = useAppointmentChannel({
    appointmentId,
    channelId: channel?.id,
    channelStatusAction: channelStatus?.channelStatusAction,
    petParentIds,
    clinicPetId: clinicPet?.id,
  });

  return (
    <VStack className="AppointmentCard__Container" align="flex-start" w="100%">
      {showTimeGroup && (
        <Text className="AppointmentCard__TimeStamp" size="xs" variant="subtle" mt={1} textAlign="center" w="100%">
          {appointmentTime}
        </Text>
      )}
      <Card
        className="AppointmentCard__Card"
        data-testid="appointment-card"
        data-mixpanel-name="Appointment card"
        size="sm"
        isInteractive
        w="100%"
        onClick={partial(onAppointmentClicked, appointment)}
      >
        <VStack align="flex-start" spacing={3}>
          <HStack className="AppointmentCard__Header" align="flex-start" justify="space-between" w="100%">
            <HStack className="AppointmentCard__PetDetails" align="flex-start" spacing={0}>
              <PetAvatar
                speciesType={clinicPet?.speciesType}
                isDeceased={clinicPet?.computedIsDeceased}
                isActive={clinicPet?.computedIsActive}
                name={clinicPet?.name}
                photoUrl={clinicPet?.photoUrl}
                onClick={(e: MouseEvent<HTMLSpanElement>): void => {
                  e.stopPropagation();
                  if (isStandardClinic) {
                    viewClinicPet({ clinicPetId: clinicPet?.id || '' });
                  }
                }}
              />
              <VStack spacing={0} align="flex-start">
                <Text size="lg" fontWeight="bold" aria-disabled={clinicPet?.computed_rawPimsIsDeceased || false}>
                  {appointment.clinicPet?.name || 'No Patient Name'}
                </Text>
                <Text size="sm">{petParentNames.join(', ')}</Text>
              </VStack>
            </HStack>
            <VStack className="AppointmentCard__ContactButtons" align="flex-end">
              <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
                <Button variant="secondarySubtle" size="xs" iconName="message" onClick={onChatClick}>
                  Open Conversation
                </Button>
              </HasModule>
              <HStack>
                <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
                  <Button
                    data-mixpanel-name="Appointment card call button"
                    data-testid="appointment-card-call-button"
                    variant="secondarySubtle"
                    size="xs"
                    iconName="phone"
                    onClick={onCallClick}
                  >
                    Call
                  </Button>
                </HasModule>
                <Button
                  data-mixpanel-name="Appointment card recap button"
                  data-testid="appointment-card-recap-button"
                  variant="secondarySubtle"
                  size="xs"
                  iconName="aiMicrophone"
                  onClick={onRecapClick}
                >
                  AI Recap
                </Button>
              </HStack>
            </VStack>
          </HStack>
          <VStack className="AppointmentCard__Body" spacing={0} align="flex-start">
            <Text className="AppointmentCard__TimeStamp" size="sm">
              {appointmentTime}
            </Text>
            <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
              <Text variant="subtle" size="sm">
                {truncateString(description, 40) || 'No description available'}
              </Text>
            </HasModule>
          </VStack>
          <HStack
            className="AppointmentCard__Footer"
            flexWrap="wrap"
            justifyContent="space-between"
            align="center"
            spacing={0}
            rowGap={1}
            w="100%"
          >
            <Wrap>
              <WrapItem>
                <Tag
                  leftElement={
                    <Icon
                      variant={getAppointmentStatusTagVariant(!!pimsStatus, pimsStatus || status) as TypographyVariant}
                      size="xs"
                      name="clock"
                    />
                  }
                  size="sm"
                  label={
                    integration?.type === ClinicPimsIntegrationType.Instinct
                      ? status || pimsStatus
                      : pimsStatus || status
                  }
                  variant={getAppointmentStatusTagVariant(!!pimsStatus, pimsStatus || status)}
                />
              </WrapItem>
              <WrapItem>
                <Tag
                  leftElement={<Icon size="xs" name="calendarCheck" />}
                  size="sm"
                  variant="default"
                  label={appointmentTypeDisplayName}
                />
              </WrapItem>
            </Wrap>
            {channelStatus && channel && (
              <Box
                onClick={(e): void => {
                  e.stopPropagation();
                }}
              >
                <ChannelStatusSelect
                  currentStatus={{ id: channelStatus.id, name: channelStatus.name, color: channelStatus.color }}
                  channelId={channel.id}
                  maxW="110px"
                  placement={Placement.BottomStart}
                />
              </Box>
            )}
          </HStack>
        </VStack>
      </Card>
    </VStack>
  );
};
